import React from "react";
import { MessageCircle, MinusCircle } from "lucide-react";

import type { Ok } from "@ps-bridge/ok";
import type { DictationFieldId, DictationRecordingState } from "@services/clients/DocumentAssistantDictationApi";

type DocumentAssistantDictationControlProps = {
  readonly fieldId: DictationFieldId;
  readonly state: DictationRecordingState;
  readonly errorMessage: string;
  readonly isBusy: boolean;
  readonly isRecording: boolean;
  readonly onToggle: () => Ok;
  readonly buttonClassName: string;
  readonly iconClassName: string;
};

const fieldLabels: Record<DictationFieldId, string> = {
  prompt: "prompt answer",
  documentDetailsPrompt: "document details answer",
  refine: "refine feedback",
  finalEdits: "final edits",
};

const stateText = (state: DictationRecordingState): string => {
  switch (state) {
    case "recording":
      return "Recording dictation.";
    case "stopping":
      return "Stopping recording.";
    case "transcribing":
      return "Transcribing dictation.";
    case "failed":
      return "Dictation needs attention.";
    case "idle":
      return "Dictation idle.";
  }
};

const DocumentAssistantDictationControl: React.FC<DocumentAssistantDictationControlProps> = ({
  fieldId,
  state,
  errorMessage,
  isBusy,
  isRecording,
  onToggle,
  buttonClassName,
  iconClassName,
}) => {
  const label = fieldLabels[fieldId];
  const statusId = `${fieldId}-dictation-status`;
  const errorId = `${fieldId}-dictation-error`;
  const status = stateText(state);
  const iconPhase = isRecording ? "recording" : "idle";
  const iconByPhase = {
    idle: <MessageCircle size={16} className={iconClassName} />,
    recording: <MinusCircle size={16} className={iconClassName} />,
  } as const;

  return (
    <div data-testid={`${fieldId}-dictation-control`}>
      <button
        type="button"
        data-testid={`${fieldId}-dictation-toggle`}
        className={buttonClassName}
        onClick={onToggle}
        aria-label={isRecording ? `Stop dictation for ${label}` : `Start dictation for ${label}`}
        aria-pressed={isRecording}
        aria-controls={statusId}
        disabled={isBusy && !isRecording}
      >
        {iconByPhase[iconPhase]}
      </button>
      <div id={statusId} data-testid={`${fieldId}-dictation-status`} role="status" aria-live="polite">
        {status}
      </div>
      <div
        id={errorId}
        data-testid={`${fieldId}-dictation-error`}
        role="alert"
        aria-hidden={errorMessage.length === 0}
      >
        {errorMessage}
      </div>
    </div>
  );
};

export default DocumentAssistantDictationControl;
